/**
 * Mock product data with Unsplash royalty-free images.
 * Images from https://unsplash.com (free to use).
 */

/** Single variant / option row from customer-products API `attributes`. */
export interface ProductAttribute {
  id: number;
  name: string;
  value: string;
  notes?: string;
  /** Formatted like the main product price, e.g. "$195". */
  priceLabel?: string;
  /** Same units as cart `unitBasePrice` (API attribute `price`). */
  unitPrice?: number;
  quantity?: number;
  /** Resolved object-storage URLs */
  images?: string[];
  /** Store id when API sends it on the variant row (cart falls back to `Product.business_id`). */
  business_id?: string;
}

export function formatAttributeName(name: string): string {
  return name.replace(/_/g, " ");
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: string;
  image: string;
  imageThumbs?: string[];
  product_type: string;
  /** From API; in-stock units for the base SKU (cart lines without a variant). */
  quantity?: number;
  /** From API; used for cart order sync */
  business_id?: string;
  /** From API `business_name`; display label for the selling vendor. */
  vendor_name?: string;
  /** From API; variant rows shown on product detail */
  attributes?: ProductAttribute[];
}

export const PRODUCTS: Product[] = [
  {
    id: "1",
    name: "Handcrafted Ceramic Vase",
    description: "Artisan-made vase with earthy glaze.",
    price: "$48.00",
    image: "https://images.unsplash.com/photo-1578749556568-bc2c40e68b61?w=600",
    imageThumbs: [
      "https://images.unsplash.com/photo-1578749556568-bc2c40e68b61?w=150",
      "https://images.unsplash.com/photo-1610701596007-11502861dcfa?w=150",
      "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=150",
      "https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=150",
    ],
    product_type: "1",
  },
  {
    id: "2",
    name: "Organic Cotton Throw",
    description: "Soft, sustainable throw for your space.",
    price: "$62.00",
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600",
    product_type: "2",
  },
  {
    id: "3",
    name: "Wooden Serving Board",
    description: "Reclaimed oak serving board.",
    price: "$38.00",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600",
    product_type: "3",
  },
  {
    id: "4",
    name: "Minimalist Desk Lamp",
    description: "Warm LED desk lamp with adjustable arm.",
    price: "$89.00",
    image: "https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=600",
    product_type: "1",
  },
  {
    id: "5",
    name: "Leather Journal",
    description: "Hand-bound journal with blank pages.",
    price: "$34.00",
    image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600",
    product_type: "4",
  },
  {
    id: "6",
    name: "Stoneware Mug Set",
    description: "Set of four stoneware mugs.",
    price: "$52.00",
    image: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=600",
    product_type: "1",
  },
  {
    id: "7",
    name: "Woven Storage Basket",
    description: "Natural fiber storage basket.",
    price: "$45.00",
    image: "https://images.unsplash.com/photo-1585916420730-d7f95e942d43?w=600",
    product_type: "2",
  },
  {
    id: "8",
    name: "Brass Candle Holder",
    description: "Elegant brass candle holder.",
    price: "$28.00",
    image: "https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?w=600",
    product_type: "3",
  },
  {
    id: "9",
    name: "Linen Table Runner",
    description: "Natural linen table runner.",
    price: "$41.00",
    image: "https://images.unsplash.com/photo-1631889993959-41b4e9c6e3c5?w=600",
    product_type: "2",
  },
  {
    id: "10",
    name: "Terracotta Planter",
    description: "Hand-thrown terracotta planter.",
    price: "$36.00",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600",
    product_type: "1",
  },
  {
    id: "11",
    name: "Wool Blend Blanket",
    description: "Cozy wool blend throw blanket.",
    price: "$78.00",
    image: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=600",
    product_type: "2",
  },
  {
    id: "12",
    name: "Olive Wood Bowl",
    description: "Solid olive wood salad bowl.",
    price: "$54.00",
    image: "https://images.unsplash.com/photo-1578500494198-246f612d3b3d?w=600",
    product_type: "3",
  },
];

// Relative hero image path; combined with assets base URL from deployment config.
const HERO_IMAGE = "login-five.jpg";

export function getHeroImage() {
  return HERO_IMAGE;
}

export function getProductById(id: string): Product | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

export function getTopPicks(): Product[] {
  return PRODUCTS.slice(0, 4);
}

export function getClearanceSales(): Product[] {
  return PRODUCTS.slice(4, 8);
}

export function getPromotions(): Product[] {
  return PRODUCTS.slice(8, 12);
}

export function getSimilarProducts(productId: string, limit = 6): Product[] {
  const current = getProductById(productId);
  if (!current) return PRODUCTS.slice(0, limit);
  return PRODUCTS.filter((p) => p.id !== productId).slice(0, limit);
}
